import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions
} from '@tanstack/react-query';

import type { FindNewsAllQuery } from '@schema/cart';

import API from '@api';
import { getQueryClient } from '@root/src/providers/query';
import { withSsrCache } from '../../ssrCache';

export const ELEMENTS_NEWS = 6;

const NEWS_COUNT_STALE_TIME = 5 * 60 * 1000;
const NEWS_COUNT_GC_TIME = 10 * 60 * 1000;

export type NewsItemT = NonNullable<FindNewsAllQuery['findNewsAll']>[number];

/** Общее количество новостей — на бэкенде это отдельный метод */
export const newsCountQueryOptions = () =>
  queryOptions({
    queryKey: ['news', 'count'],
    queryFn: () => API.getCountNewsItems(),
    staleTime: NEWS_COUNT_STALE_TIME,
    gcTime: NEWS_COUNT_GC_TIME
  });

export const singleNewsQueryOptions = (id: number) =>
  withSsrCache(
    queryOptions({
      queryKey: ['news', 'single', id],
      queryFn: () => API.getSingleNews({ id }),
      select: res => res.findNewsById,
      staleTime: NEWS_COUNT_STALE_TIME,
      gcTime: NEWS_COUNT_GC_TIME
    })
  );

/** Плоский список новостей (слайдер «Это интересно» на странице новости) */
export const newsAllQueryOptions = (
  params: Parameters<typeof API.getAllNews>[0]
) =>
  withSsrCache(
    queryOptions({
      queryKey: ['news', 'all', params],
      queryFn: () => API.getAllNews(params),
      staleTime: NEWS_COUNT_STALE_TIME,
      gcTime: NEWS_COUNT_GC_TIME
    })
  );

export const CHARITY_DONATIONS_QUERY_KEY = 'charityDonations';

export const charityDonationsQueryOptions = (
  data: Parameters<typeof API.getCharityDonations>[0]['data']
) =>
  queryOptions({
    queryKey: [CHARITY_DONATIONS_QUERY_KEY, data],
    queryFn: () => API.getCharityDonations({ data }),
    select: res => res.getCharityDonations?.data ?? null
  });

export const newsInfiniteQueryOptions = (
  params: Omit<Parameters<typeof API.getAllNews>[0], 'page'>,
  initialPage = 1
) => {
  const cacheKey = ['news', 'infinite', params];

  return withSsrCache(
    infiniteQueryOptions({
      queryKey: cacheKey,
      queryFn: async ({ pageParam }) => {
        const perPage = params.setLimit ?? ELEMENTS_NEWS;

        // totalCount приходит отдельным методом; ensureQueryData
        // дедуплицирует его между страницами списка (5 минут кэша вместо
        // запроса на каждую страницу).
        // ЭТАП 2 — getQueryClient, не синглтон: на SSR это per-request клиент
        // (изоляция кэша между запросами), на клиенте — тот же синглтон.
        const [newsRes, countRes] = await Promise.all([
          API.getAllNews({ ...params, page: pageParam }),
          getQueryClient().ensureQueryData(newsCountQueryOptions())
        ]);

        const totalCount = countRes.countNewsItems || 0;
        const totalCountPages = Math.ceil(totalCount / perPage);

        return {
          items: newsRes.findNewsAll || [],
          total: totalCount,
          pagination: {
            total: totalCountPages,
            current: pageParam
          }
        };
      },
      initialPageParam: initialPage,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        const limit = params.setLimit ?? ELEMENTS_NEWS;

        return lastPageParam * limit < lastPage.total
          ? lastPageParam + 1
          : undefined;
      },
      placeholderData: keepPreviousData
    })
  );
};

/**
 * ЭТАП 2 — размер страницы списка новостей из /r/last/N. ЕДИНЫЙ источник для
 * компонента (routes/News) и SSR-префетча — ключ {setLimit} обязан совпасть
 * байт-в-байт.
 */
export const resolveNewsSetLimit = (last: string | undefined): number =>
  last === '12' ? 12 : last === '18' ? 18 : last === '24' ? 24 : 6;
